/**
 * The pages nobody plans for, which is why they matter.
 *
 * Each one exists because the alternative is a blank screen, and a blank
 * screen in a hospital at 3am becomes a support call. Every one of them says
 * what happened and offers the next move.
 */

import { useAuth } from '../auth/useAuth';
import { PageState } from '../shared/PageState';
import styles from '../shared/Page.module.css';

/** Authenticated, but a member of no organization. */
export function NoAccessPage() {
  const { session, signOut } = useAuth();

  return (
    <main className={styles.state} role="alert">
      <h1 className={styles.stateTitle}>Your account is not yet in an organization</h1>
      <p className={styles.stateBody}>
        You are signed in as <strong>{session?.email ?? 'an unknown account'}</strong>,
        but that address has not been added to a hospital or clinic on StandingDocs.
        An administrator there needs to invite you before there is anything to
        show you.
      </p>
      <button type="button" className={styles.btn} onClick={signOut}>
        Sign out
      </button>
    </main>
  );
}

/** Signed in, in an org, but lacking the role a route needs. */
export function NotAuthorizedPage() {
  return (
    <PageState
      title="You do not have access to that page"
      tone="alert"
      action={{ to: '/policies', label: 'Back to policies' }}
    >
      Your role does not include it. If you need it for your work, ask an
      administrator at your organization to change your role — they can do that
      themselves.
    </PageState>
  );
}

export function NotFoundPage() {
  return (
    <PageState title="Page not found" action={{ to: '/policies', label: 'Go to policies' }}>
      That address does not match anything in this application. It may have been
      renamed, or the link that brought you here may be out of date.
    </PageState>
  );
}
